import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Plus, Trash2, Edit2, Mail, Settings as SettingsIcon, X, Image as ImgIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import ImageGalleryEditor from "@/components/ImageGalleryEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullImageUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const EMPTY_DRESS = {
  number: "",
  name: "",
  size: "",
  color: "",
  price: 0,
  sale_price: 0,
  deposit: 0,
  available: true,
  description: "",
  rental_rules: "",
  image_url: "",
  images: [],
  sort_order: 0,
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("dresses");
  const [dresses, setDresses] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null); // dress object or "new"

  const reload = async () => {
    try {
      const [d, i, s] = await Promise.all([
        api.get("/dresses"),
        api.get("/admin/inquiries"),
        api.get("/settings"),
      ]);
      setDresses(d.data);
      setInquiries(i.data);
      setSettings(s.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => { if (user) reload(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const newInquiryCount = inquiries.filter((i) => !i.read).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-ink text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl">
            10<span className="text-gold">/</span>10 <span className="overline text-cream/60 ml-2">Admin</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-cream/70">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 border border-cream/30 text-xs uppercase tracking-widest hover:bg-cream hover:text-ink transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-8 overflow-x-auto">
          <TabBtn id="dresses" tab={tab} setTab={setTab} label={`Kleider · ${dresses.length}`} />
          <TabBtn id="inquiries" tab={tab} setTab={setTab} label={`Anfragen · ${inquiries.length}`} badge={newInquiryCount} />
          <TabBtn id="settings" tab={tab} setTab={setTab} label="Einstellungen" />
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {tab === "dresses" && (
          <DressesTab
            dresses={dresses}
            onEdit={async (d) => {
              // Fetch the full dress (with raw base64 images) for editing.
              try {
                const { data } = await api.get(`/admin/dresses/${d.id}`);
                setEditing(data);
              } catch {
                // Fallback to list version (will use proxy URLs, server resolves them on save)
                setEditing(d);
              }
            }}
            onNew={() => setEditing({ ...EMPTY_DRESS })}
            onDelete={async (d) => {
              if (!window.confirm(`Kleid ${d.number} wirklich löschen?`)) return;
              try {
                await api.delete(`/admin/dresses/${d.id}`);
                toast.success("Kleid gelöscht");
                reload();
              } catch (e) { toast.error(formatErr(e)); }
            }}
            onDeleteAll={async () => {
              if (!window.confirm(`Wirklich ALLE ${dresses.length} Kleider unwiderruflich löschen?`)) return;
              try {
                await api.delete("/admin/dresses");
                toast.success("Alle Kleider gelöscht");
                reload();
              } catch (e) { toast.error(formatErr(e)); }
            }}
          />
        )}

        {tab === "inquiries" && (
          <InquiriesTab
            inquiries={inquiries}
            onMarkRead={async (id) => {
              try { await api.put(`/admin/inquiries/${id}/read`); reload(); } catch (e) { toast.error(formatErr(e)); }
            }}
            onDelete={async (id) => {
              if (!window.confirm("Anfrage löschen?")) return;
              try { await api.delete(`/admin/inquiries/${id}`); toast.success("Gelöscht"); reload(); } catch (e) { toast.error(formatErr(e)); }
            }}
          />
        )}

        {tab === "settings" && (
          <SettingsTab settings={settings} onSaved={reload} />
        )}
      </main>

      {editing && (
        <DressDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ id, tab, setTab, label, badge }) {
  const active = tab === id;
  return (
    <button
      data-testid={`tab-${id}`}
      onClick={() => setTab(id)}
      className={`relative py-5 text-xs uppercase tracking-widest transition-colors ${active ? "text-ink" : "text-ink/50 hover:text-ink"}`}
    >
      {label}
      {!!badge && (
        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 bg-gold text-cream text-[10px] px-1.5">{badge}</span>
      )}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink" />}
    </button>
  );
}

function DressesTab({ dresses, onEdit, onNew, onDelete, onDeleteAll }) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <h2 className="font-display text-3xl md:text-4xl">Kleider verwalten</h2>
        <div className="flex items-center gap-2">
          {dresses.length > 0 && (
            <button
              data-testid="delete-all-dresses"
              onClick={onDeleteAll}
              className="inline-flex items-center gap-2 border border-[#E5E0D8] text-ink/60 px-4 py-3 text-xs uppercase tracking-widest hover:text-red-600 hover:border-red-600 transition-colors"
            >
              <Trash2 size={14} /> Alle Demo-Kleider löschen
            </button>
          )}
          <button
            data-testid="add-dress-btn"
            onClick={onNew}
            className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-widest hover:bg-gold transition-colors"
          >
            <Plus size={14} /> Neues Kleid
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {dresses.map((d) => (
          <div key={d.id} data-testid={`admin-dress-${d.number}`} className="bg-white border border-[#E5E0D8]">
            <div className="aspect-[3/4] bg-stone-100 relative overflow-hidden">
              {d.image_url ? (
                <img src={fullImageUrl(d.image_url)} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-ink/30">
                  {d.number}
                </div>
              )}
              <span className="absolute top-3 left-3 bg-cream/95 px-2 py-1 overline">{d.number}</span>
              <span className={`absolute top-3 right-3 px-2 py-1 text-[10px] uppercase tracking-widest ${d.available ? "bg-gold text-cream" : "bg-ink text-cream"}`}>
                {d.available ? "Verfügbar" : "Vergeben"}
              </span>
            </div>
            <div className="p-4">
              <div className="font-display text-xl">{d.name}</div>
              <div className="text-xs text-ink/60 mt-1">{d.size} · {d.color} · {d.price.toFixed(0)} € · Kaution {d.deposit.toFixed(0)} €</div>
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`edit-dress-${d.number}`}
                  onClick={() => onEdit(d)}
                  className="flex-1 inline-flex items-center justify-center gap-1 border border-ink text-ink py-2 text-xs uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors"
                >
                  <Edit2 size={12} /> Bearbeiten
                </button>
                <button
                  data-testid={`delete-dress-${d.number}`}
                  onClick={() => onDelete(d)}
                  className="inline-flex items-center justify-center px-3 border border-[#E5E0D8] text-ink/60 hover:text-red-600 hover:border-red-600 transition-colors"
                  aria-label="Löschen"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InquiriesTab({ inquiries, onMarkRead, onDelete }) {
  return (
    <div>
      <h2 className="font-display text-3xl md:text-4xl mb-8">Eingegangene Anfragen</h2>
      {inquiries.length === 0 ? (
        <div className="p-10 border border-dashed border-[#E5E0D8] text-center text-ink/50">
          Noch keine Anfragen erhalten.
        </div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inq) => (
            <div
              key={inq.id}
              data-testid={`inquiry-${inq.id}`}
              className={`bg-white border p-6 grid md:grid-cols-[1fr_auto] gap-4 ${inq.read ? "border-[#E5E0D8]" : "border-gold"}`}
            >
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-display text-xl">{inq.name}</span>
                  {!inq.read && <span className="px-2 py-0.5 bg-gold text-cream text-[10px] uppercase tracking-widest">Neu</span>}
                </div>
                <div className="text-sm text-ink/70 flex flex-wrap gap-x-4 gap-y-1">
                  <span><Mail size={12} className="inline mr-1 text-gold" />{inq.phone}</span>
                  {inq.dress_ref && <span>· Kleid: <strong>{inq.dress_ref}</strong></span>}
                  {inq.wish_date && <span>· Wunschdatum: <strong>{inq.wish_date}</strong></span>}
                </div>
                {inq.message && <p className="mt-3 text-ink/80 whitespace-pre-line">{inq.message}</p>}
                <div className="mt-2 text-xs text-ink/40">{new Date(inq.created_at).toLocaleString("de-DE")}</div>
              </div>
              <div className="flex md:flex-col gap-2">
                {!inq.read && (
                  <button onClick={() => onMarkRead(inq.id)} className="inline-flex items-center justify-center gap-1 border border-ink text-ink py-2 px-3 text-xs uppercase tracking-widest hover:bg-ink hover:text-cream">
                    <Check size={12} /> Gelesen
                  </button>
                )}
                <button onClick={() => onDelete(inq.id)} className="inline-flex items-center justify-center px-3 py-2 text-ink/50 hover:text-red-600 border border-[#E5E0D8] hover:border-red-600">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Einstellungen gespeichert");
      onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-3xl md:text-4xl mb-8 flex items-center gap-3">
        <SettingsIcon className="text-gold" /> Einstellungen
      </h2>
      <form onSubmit={save} className="space-y-10 bg-white p-8 border border-[#E5E0D8]">
        <div>
          <div className="overline mb-4"><span className="gold-line" />Kontakt</div>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="WhatsApp Nummer (international, ohne +)">
              <input data-testid="settings-whatsapp" value={form.whatsapp_number || ""} onChange={upd("whatsapp_number")} placeholder="4917632161798" className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none font-mono" />
            </Field>
            <Field label="Telefonnummer (für Anzeige)">
              <input data-testid="settings-phone" value={form.phone || ""} onChange={upd("phone")} placeholder="+49 ..." className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
            <Field label="E-Mail">
              <input data-testid="settings-email" type="email" value={form.email || ""} onChange={upd("email")} placeholder="info@..." className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
            <Field label="Instagram (Handle oder URL)">
              <input data-testid="settings-instagram" value={form.instagram || ""} onChange={upd("instagram")} placeholder="@1010kleiderverleih" className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
            <Field label="Ladenadresse">
              <input data-testid="settings-address" value={form.store_address || ""} onChange={upd("store_address")} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
            <Field label="Öffnungszeiten">
              <input data-testid="settings-hours" value={form.store_hours || ""} onChange={upd("store_hours")} placeholder="Mo–Sa 10–19 Uhr" className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
          </div>
        </div>

        <div>
          <div className="overline mb-4"><span className="gold-line" />Rechtliches</div>
          <div className="space-y-5">
            <Field label="Firmen-/Inhaber-Name (für Impressum)">
              <input data-testid="settings-legal-name" value={form.legal_name || ""} onChange={upd("legal_name")} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none" />
            </Field>
            <Field label="Impressum (Text)">
              <textarea data-testid="settings-legal-text" rows={6} value={form.legal_text || ""} onChange={upd("legal_text")} placeholder="Wird auf /impressum angezeigt. Leer lassen für Platzhalter-Text." className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none resize-y" />
            </Field>
            <Field label="Datenschutzerklärung (Text)">
              <textarea data-testid="settings-privacy-text" rows={6} value={form.privacy_text || ""} onChange={upd("privacy_text")} placeholder="Wird auf /datenschutz angezeigt. Leer lassen für Platzhalter-Text." className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-3 outline-none resize-y" />
            </Field>
          </div>
        </div>

        <button
          data-testid="settings-save"
          type="submit"
          disabled={saving}
          className="bg-ink text-cream px-8 py-3 text-xs uppercase tracking-widest hover:bg-gold disabled:opacity-50"
        >
          {saving ? "Speichern..." : "Speichern"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      {children}
    </div>
  );
}

function DressDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;

  // Initialize form so `images` is always an array (backfill from legacy image_url).
  // Using useEffect avoids "setState during render" — runs once when initial changes.
  useEffect(() => {
    if (!Array.isArray(form.images)) {
      const next = { ...form };
      if (form.image_url) next.images = [form.image_url];
      else next.images = [];
      setForm(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    if (!form.number || !form.name) {
      toast.error("Nummer und Name sind Pflichtfelder.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        sale_price: form.sale_price === "" || form.sale_price == null ? null : (parseFloat(form.sale_price) || null),
        deposit: parseFloat(form.deposit) || 0,
      };
      if (isNew) {
        await api.post("/admin/dresses", payload);
        toast.success("Kleid angelegt");
      } else {
        await api.put(`/admin/dresses/${initial.id}`, payload);
        toast.success("Kleid gespeichert");
      }
      onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={onClose}>
      <div data-testid="dress-dialog" onClick={(e) => e.stopPropagation()} className="bg-cream w-full max-w-3xl border border-[#E5E0D8]">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E0D8]">
          <h3 className="font-display text-2xl">{isNew ? "Neues Kleid" : `Kleid ${initial.number} bearbeiten`}</h3>
          <button onClick={onClose} className="p-2 hover:text-gold" aria-label="Schließen"><X size={18} /></button>
        </div>

        <form onSubmit={save} className="p-6 grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2 p-4 border border-[#E5E0D8] bg-white">
            <ImageGalleryEditor
              images={form.images || []}
              onChange={(imgs) => setForm({ ...form, images: imgs, image_url: imgs[0] || "" })}
            />
          </div>

          <Field label="Kleidnummer">
            <input data-testid="dress-number" value={form.number} onChange={(e) => update("number", e.target.value)} required className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Sortierreihenfolge (1 = zuerst)">
            <input
              data-testid="dress-sort-order"
              type="number"
              min="0"
              value={form.sort_order ?? 0}
              onChange={(e) => update("sort_order", e.target.value)}
              placeholder="z. B. 1, 2, 3 …"
              className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none"
            />
          </Field>
          <Field label="Name">
            <input data-testid="dress-name" value={form.name} onChange={(e) => update("name", e.target.value)} required className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Größe">
            <input data-testid="dress-size" value={form.size} onChange={(e) => update("size", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Farbe">
            <input data-testid="dress-color" value={form.color} onChange={(e) => update("color", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Mietpreis (€)">
            <input data-testid="dress-price" type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Verkaufspreis (€)">
            <input data-testid="dress-sale-price" type="number" step="0.01" value={form.sale_price || ""} onChange={(e) => update("sale_price", e.target.value)} placeholder="z. B. 900" className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <Field label="Kaution (€)">
            <input data-testid="dress-deposit" type="number" step="0.01" value={form.deposit} onChange={(e) => update("deposit", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none" />
          </Field>
          <div className="md:col-span-2">
            <label className="overline flex items-center gap-3 cursor-pointer">
              <input data-testid="dress-available" type="checkbox" checked={!!form.available} onChange={(e) => update("available", e.target.checked)} />
              Verfügbar
            </label>
          </div>
          <Field label="Beschreibung">
            <textarea data-testid="dress-description" rows={3} value={form.description || ""} onChange={(e) => update("description", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none resize-none" />
          </Field>
          <Field label="Mietbedingungen">
            <textarea data-testid="dress-rules" rows={3} value={form.rental_rules || ""} onChange={(e) => update("rental_rules", e.target.value)} className="w-full bg-transparent border-b border-[#E5E0D8] focus:border-ink py-2 outline-none resize-none" />
          </Field>

          <div className="md:col-span-2 flex gap-3 pt-2">
            <button
              data-testid="dress-save"
              type="submit"
              disabled={saving}
              className="bg-ink text-cream px-8 py-3 text-xs uppercase tracking-widest hover:bg-gold disabled:opacity-50"
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
            <button type="button" onClick={onClose} className="border border-ink px-8 py-3 text-xs uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors">
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
